import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const typographyVariants = cva("", {
  variants: {
    variant: {
      "heading-2xl":
        "text-[32px] leading-10 font-medium text-foreground",
      "heading-xl":
        "text-[28px] leading-9 font-medium text-foreground",
      "heading-lg":
        "text-2xl leading-[34px] font-normal text-foreground",
      "heading-md":
        "text-base leading-6 font-semibold text-foreground",
      body: "text-sm leading-5 font-normal text-foreground",
      "body-medium":
        "text-sm leading-5 font-medium text-foreground",
      "body-semibold":
        "text-sm leading-5 font-semibold text-foreground",
      // Tertiary size between `body` and `small`: card descriptions,
      // status lines and lock notes on dense surfaces (Integrations landing).
      // Named so the 13px value lives here, not as text-[13px] at call sites.
      "body-sm": "text-[13px] leading-[18px] font-normal text-foreground",
      "body-sm-medium":
        "text-[13px] leading-[18px] font-medium text-foreground",
      small:
        "text-xs leading-[18px] font-normal text-muted-foreground",
      "small-medium":
        "text-xs leading-[18px] font-medium text-muted-foreground",
      "small-semibold":
        "text-xs leading-[18px] font-semibold text-muted-foreground",
      caption:
        "text-[10px] leading-[14px] font-normal tracking-[0.2px] text-muted-foreground",
    },
  },
  defaultVariants: {
    variant: "body",
  },
})

type TypographyVariant = NonNullable<
  VariantProps<typeof typographyVariants>["variant"]
>

interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType
}

/**
 * Text primitive exposing the design-system type scale via a `variant` axis.
 * `variant` sets typography classes only — it does not pick an element. Every
 * variant renders `<span>` unless you pass `as`, so headings and stacked lines
 * need an explicit `as` (or `className="block"`) to avoid collapsing inline.
 *
 * @category Brand
 */
function Typography({
  className,
  variant,
  as: Comp = "span",
  ...props
}: TypographyProps) {
  return (
    <Comp
      className={cn(typographyVariants({ variant, className }))}
      {...props}
    />
  )
}

export { Typography, typographyVariants }
export type { TypographyProps, TypographyVariant }
